import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { useSupabase } from '../contexts/SupabaseContext';

// Generate a unique ID for this instance of the AuthProvider
const AUTH_PROVIDER_ID = `AuthProvider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

interface AuthContextType {
  user: User | null;
  userRole: 'USER' | 'ADMIN' | null;
  loading: boolean;
  sessionExpired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    institution: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.debug(`[${AUTH_PROVIDER_ID}] AuthProvider initializing at ${new Date().toISOString()}`);
  
  const supabase = useSupabase();
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<'USER' | 'ADMIN' | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  
  // Add a ref to track the current user ID to prevent stale closures
  const userIdRef = useRef<string | null>(null);
  // Add a ref to track if role fetch is in progress
  const isFetchingRoleRef = useRef(false);

  // Log auth state on each render
  console.debug(`[${AUTH_PROVIDER_ID}] Current auth state: { userId: ${user?.id || 'null'}, userRole: ${userRole || 'null'}, loading: ${loading}, sessionExpired: ${sessionExpired} } at ${new Date().toISOString()}`);

  const fetchUserRole = async (userId: string) => {
    // Skip if already fetching for this user
    if (isFetchingRoleRef.current) {
      console.debug(`[${AUTH_PROVIDER_ID}] Already fetching role, skipping duplicate call for userId: ${userId}`);
      return userRole;
    }
    
    const startTime = new Date().toISOString();
    const performanceStart = performance.now();
    console.debug(`[${AUTH_PROVIDER_ID}] fetchUserRole started for userId: ${userId} at ${startTime}`);
    
    try {
      isFetchingRoleRef.current = true;
      console.debug(`[${AUTH_PROVIDER_ID}] fetchUserRole executing supabase query for userId: ${userId}`);
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      const performanceEnd = performance.now();
      console.debug(`[${AUTH_PROVIDER_ID}] fetchUserRole query completed in ${(performanceEnd - performanceStart).toFixed(2)}ms`);

      if (error) {
        console.error(`[${AUTH_PROVIDER_ID}] Error fetching user role: [${error.code}] ${error.message}`, error);
        return null;
      }

      console.debug(`[${AUTH_PROVIDER_ID}] fetchUserRole successful, role: ${data?.role || 'null'}`);
      return data?.role as 'USER' | 'ADMIN' | null;
    } catch (err) {
      console.error(`[${AUTH_PROVIDER_ID}] Unexpected error in fetchUserRole:`, err);
      return null;
    } finally {
      isFetchingRoleRef.current = false;
      const endTime = new Date().toISOString();
      console.debug(`[${AUTH_PROVIDER_ID}] fetchUserRole completed at ${endTime} (total time: ${Date.parse(endTime) - Date.parse(startTime)}ms)`);
    }
  };

  useEffect(() => {
    const initAuthEffect = async () => {
      const startTime = new Date().toISOString();
      console.debug(`[${AUTH_PROVIDER_ID}] Auth initialization effect started at ${startTime}`);
      console.debug(`[${AUTH_PROVIDER_ID}] Initial state: loading=${loading}, user=${user?.id || 'null'}, userRole=${userRole || 'null'}`);
      
      try {
        // Check active sessions and sets the user
        console.debug(`[${AUTH_PROVIDER_ID}] Calling supabase.auth.getSession() at ${new Date().toISOString()}`);
        const sessionStart = performance.now();
        const { data: { session } } = await supabase.auth.getSession();
        const sessionEnd = performance.now();
        console.debug(`[${AUTH_PROVIDER_ID}] getSession completed in ${(sessionEnd - sessionStart).toFixed(2)}ms, session: ${session ? 'exists' : 'null'}`);
        
        if (session?.user) {
          console.debug(`[${AUTH_PROVIDER_ID}] Session user found, id: ${session.user.id}, email: ${session.user.email}`);
          
          // Update the ref to track current user ID
          userIdRef.current = session.user.id;
          
          // Only update state if different (using functional update)
          setUser(currentUser => {
            if (!currentUser || currentUser.id !== session.user.id) {
              console.debug(`[${AUTH_PROVIDER_ID}] User state updated with session user at ${new Date().toISOString()}`);
              return session.user;
            }
            return currentUser;
          });
          
          console.debug(`[${AUTH_PROVIDER_ID}] Fetching role for user ${session.user.id}`);
          const roleStart = performance.now();
          const role = await fetchUserRole(session.user.id);
          const roleEnd = performance.now();
          console.debug(`[${AUTH_PROVIDER_ID}] Role fetch completed in ${(roleEnd - roleStart).toFixed(2)}ms, role: ${role || 'null'}`);
          
          // Only update role state if different
          setUserRole(currentRole => {
            if (currentRole !== role) {
              console.debug(`[${AUTH_PROVIDER_ID}] UserRole state updated to ${role || 'null'} at ${new Date().toISOString()}`);
              return role;
            }
            return currentRole;
          });
        } else {
          console.debug(`[${AUTH_PROVIDER_ID}] No active session found, setting user to null`);
          userIdRef.current = null;
          setUser(null);
          setUserRole(null);
          console.debug(`[${AUTH_PROVIDER_ID}] User and userRole states reset to null at ${new Date().toISOString()}`);
        }
        
        setLoading(false);
        console.debug(`[${AUTH_PROVIDER_ID}] Loading state set to false at ${new Date().toISOString()}`);

        // Listen for changes on auth state
        console.debug(`[${AUTH_PROVIDER_ID}] Setting up auth state change listener at ${new Date().toISOString()}`);
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          const changeTime = new Date().toISOString();
          console.debug(`[${AUTH_PROVIDER_ID}] Auth state change event: "${event}" at ${changeTime}`);
          console.debug(`[${AUTH_PROVIDER_ID}] Session in state change: ${session ? `userId=${session.user.id}` : 'null'}`);
          
          // Early return if this is the initial session event and we've already processed a session
          if (event === 'INITIAL_SESSION' && userIdRef.current === session?.user?.id) {
            console.debug(`[${AUTH_PROVIDER_ID}] Skipping duplicate INITIAL_SESSION event for already processed user: ${userIdRef.current}`);
            return;
          }
          
          if (session?.user) {
            console.debug(`[${AUTH_PROVIDER_ID}] Setting user from auth state change, id: ${session.user.id}`);
            console.debug(`[${AUTH_PROVIDER_ID}] Previous userId in ref: ${userIdRef.current || 'null'}, comparing with new: ${session.user.id}`);
            
            // Only update if user changed to avoid loops (using the ref for comparison)
            const userChanged = userIdRef.current !== session.user.id;
            if (userChanged) {
              console.debug(`[${AUTH_PROVIDER_ID}] User changed, updating user state and ref`);
              userIdRef.current = session.user.id;
              
              // Use functional update to ensure we're working with the latest state
              setUser(() => session.user);
            } else {
              console.debug(`[${AUTH_PROVIDER_ID}] User unchanged, skipping state update`);
            }
            
            // Only fetch role if user changed or forced refresh
            if (userChanged || event === 'TOKEN_REFRESHED') {
              console.debug(`[${AUTH_PROVIDER_ID}] Fetching role for user ${session.user.id} after auth state change`);
              const role = await fetchUserRole(session.user.id);
              
              // Only update if role changed (using functional update)
              setUserRole(currentRole => {
                if (currentRole !== role) {
                  console.debug(`[${AUTH_PROVIDER_ID}] Role changed from ${currentRole || 'null'} to ${role || 'null'}, updating state`);
                  return role;
                }
                console.debug(`[${AUTH_PROVIDER_ID}] Role unchanged (${currentRole || 'null'}), skipping state update`);
                return currentRole;
              });
            }
          } else {
            console.debug(`[${AUTH_PROVIDER_ID}] No session in auth state change, setting user and userRole to null`);
            userIdRef.current = null;
            setUser(null);
            setUserRole(null);
          }
          
          // Always ensure loading is false after auth state changes
          setLoading(current => {
            if (current) {
              console.debug(`[${AUTH_PROVIDER_ID}] Setting loading=false after auth state change`);
              return false;
            }
            return current;
          });
        });
        
        console.debug(`[${AUTH_PROVIDER_ID}] Auth state change listener set up successfully`);
        
        // Return cleanup function
        return () => {
          console.debug(`[${AUTH_PROVIDER_ID}] Cleaning up auth state change subscription at ${new Date().toISOString()}`);
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error(`[${AUTH_PROVIDER_ID}] Error in auth initialization:`, err);
        setLoading(false);
      } finally {
        const endTime = new Date().toISOString();
        console.debug(`[${AUTH_PROVIDER_ID}] Auth initialization effect completed at ${endTime} (${Date.parse(endTime) - Date.parse(startTime)}ms total)`);
      }
    };
    
    // Start the auth initialization
    initAuthEffect();
  }, [supabase]);

  const signUp = async ({
    email,
    password,
    firstName,
    lastName,
    institution
  }: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    institution: string;
  }) => {
    console.debug(`[${AUTH_PROVIDER_ID}] signUp called for email: ${email} at ${new Date().toISOString()}`);
    
    try {
      console.debug(`[${AUTH_PROVIDER_ID}] Executing supabase.auth.signUp`);
      const signUpStart = performance.now();
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            organization: institution
          }
        }
      });
      const signUpEnd = performance.now();
      console.debug(`[${AUTH_PROVIDER_ID}] signUp auth call completed in ${(signUpEnd - signUpStart).toFixed(2)}ms`);

      if (authError) {
        console.error(`[${AUTH_PROVIDER_ID}] Auth error in signUp: [${authError.name}] ${authError.message}`);
        throw authError;
      }

      // Insert into public.users table
      if (authData.user) {
        console.debug(`[${AUTH_PROVIDER_ID}] Auth successful, user created with id: ${authData.user.id}`);
        console.debug(`[${AUTH_PROVIDER_ID}] Inserting user into users table`);
        
        const profileStart = performance.now();
        const { error: profileError } = await supabase
          .from('users')
          .insert([
            {
              id: authData.user.id,
              email,
              first_name: firstName,
              last_name: lastName,
              organization: institution,
              role: 'USER'
            }
          ]);
        const profileEnd = performance.now();
        console.debug(`[${AUTH_PROVIDER_ID}] User profile insertion completed in ${(profileEnd - profileStart).toFixed(2)}ms`);

        if (profileError) {
          console.error(`[${AUTH_PROVIDER_ID}] Failed to create user profile: [${profileError.code}] ${profileError.message}`);
          throw profileError;
        }
        
        console.debug(`[${AUTH_PROVIDER_ID}] User profile created successfully`);
      }
    } catch (err) {
      console.error(`[${AUTH_PROVIDER_ID}] Unexpected error in signUp:`, err);
      throw err;
    }
  };

  const signIn = async (email: string, password: string) => {
    console.debug(`[${AUTH_PROVIDER_ID}] signIn called for email: ${email} at ${new Date().toISOString()}`);
    
    try {
      console.debug(`[${AUTH_PROVIDER_ID}] Executing supabase.auth.signInWithPassword`);
      const signInStart = performance.now();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      const signInEnd = performance.now();
      console.debug(`[${AUTH_PROVIDER_ID}] signIn completed in ${(signInEnd - signInStart).toFixed(2)}ms, result: ${data?.user ? 'success' : 'failed'}`);

      if (error) {
        console.error(`[${AUTH_PROVIDER_ID}] Error in signIn: [${error.name}] ${error.message}`);
        throw error;
      }
      
      console.debug(`[${AUTH_PROVIDER_ID}] signIn successful for user: ${data?.user?.id || 'unknown'}`);
    } catch (err) {
      console.error(`[${AUTH_PROVIDER_ID}] Unexpected error in signIn:`, err);
      throw err;
    }
  };

  const signOut = async () => {
    console.debug(`[${AUTH_PROVIDER_ID}] signOut called at ${new Date().toISOString()}`);
    
    try {
      console.debug(`[${AUTH_PROVIDER_ID}] Executing supabase.auth.signOut`);
      const signOutStart = performance.now();
      const { error } = await supabase.auth.signOut();
      const signOutEnd = performance.now();
      console.debug(`[${AUTH_PROVIDER_ID}] signOut completed in ${(signOutEnd - signOutStart).toFixed(2)}ms`);

      if (error) {
        console.error(`[${AUTH_PROVIDER_ID}] Error in signOut: [${error.name}] ${error.message}`);
        throw error;
      }
      
      console.debug(`[${AUTH_PROVIDER_ID}] signOut successful`);
    } catch (err) {
      console.error(`[${AUTH_PROVIDER_ID}] Unexpected error in signOut:`, err);
      throw err;
    }
  };

  console.debug(`[${AUTH_PROVIDER_ID}] Rendering AuthProvider at ${new Date().toISOString()}`);
  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userRole, 
        loading, 
        sessionExpired,
        signIn, 
        signUp, 
        signOut 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 